// DomainsView.jsx — CARD "Domínio próprio". View PURA: recebe { vm, actions }.
// Contrato: domains.vm.js (@version 4 — a modal de CPF saiu: os dados do pagamento são a modal
// única de components/billing, desenhada pelo container DomainsPage).
// @version 3: Reescrita completa — a View do ciclo 1 desenhava um
// FUNIL (oferta → checkout → pronto) que só existia naquela aba; esta desenha o RETRATO da
// conta que o servidor devolve: o que já é meu (domains), o que estou pagando (payment) e o
// que posso comprar (offer). Nenhum estado de etapa mora aqui.
//
// É um CARD EMBUTIDO (Home e lista de Páginas): sem título de página, sem barra de ações
// própria, largura máxima contida, e convive com vizinhos. Mesma linguagem de Serviços /
// Ausências / Integrações: mesmo raio, borda, sombra, chips de tint, blocos de estado.
//
// Regras seguidas: estilo só via `sx`; cor/raio/sombra/espaço só via var(--*); MUI v6 +
// @mui/icons-material; tipografia via <Typography variant>; nada de preço/data/prazo escrito
// no código (tudo de vm.price / vm.*Label); useState NÃO usado — não há estado de UI local
// (tudo que o card mostra vem do vm, e as três caixas de escolha são controladas por action).
//
// Neutros saem de --bg-page/--divider/--text-* (papéis themeáveis) e nunca da escala
// --slate-*, que é global fixa e clara (quebraria temas de superfície escura).
import { useState } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, InputAdornment, Tooltip, Skeleton,
  CircularProgress, Radio, Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
} from '@mui/material';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import VerifiedUserRounded from '@mui/icons-material/VerifiedUserRounded';
import RecordVoiceOverRounded from '@mui/icons-material/RecordVoiceOverRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DoneRounded from '@mui/icons-material/DoneRounded';
import DevicesRounded from '@mui/icons-material/DevicesRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import TimerOffRounded from '@mui/icons-material/TimerOffRounded';
import CloudOffRounded from '@mui/icons-material/CloudOffRounded';
import CelebrationRounded from '@mui/icons-material/CelebrationRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import EventRepeatRounded from '@mui/icons-material/EventRepeatRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import PostAddRounded from '@mui/icons-material/PostAddRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das outras telas)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
  brand: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)', sub: 'var(--color-primary-dark)', main: 'var(--color-primary)' },
  // tom da vitrine — derivado do CTA, para nada da peça de venda puxar a cor de marca
  cta: {
    bg: 'color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 14%, var(--surface))',
    border: 'color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 34%, var(--surface))',
    fg: 'color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary))',
    sub: 'color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary))',
    main: 'var(--color-cta, var(--tint-success-fg))',
  },
};

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.8 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

// CTA COMERCIAL (interesse nosso no clique) — não herda --color-primary de propósito.
// A cor da marca é escolhida por nicho: em Salão Feminino e Tatuagem ela é vinho/vermelha, e
// vermelho num botão de compra lê como "pare", não como "siga". Então o botão de dinheiro usa
// um token de AÇÃO próprio, com fallback para o verde ESCURO que já existe hoje:
//   var(--color-cta, var(--tint-success-fg))  →  #13693a dá ~7:1 com --on-primary.
// NÃO usar --color-success como preenchimento: ele foi desenhado para texto/ícone sobre
// --tint-success-bg e só chega a 3,45:1 com branco — reprova nos 4,5:1 exigidos (16px bold não
// conta como texto grande). Quando o Admin gerar --color-cta por tema, ele precisa nascer com
// ≥4,5:1 contra --on-primary, senão o problema volta pelo tema.
// Hover/foco escurecem a própria cor de ação via color-mix com --text-primary (sem literal).
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary))';

// A VITRINE TODA sai da família da cor de AÇÃO, não de --color-primary.
// Trocar só o botão não resolve nada: no Salão o painel de venda inteiro ficava framboesa
// (fundo, filete do domínio, seta, ícones, borda de seleção) — uma propaganda em tom
// proibitivo com um botão verde no pé. Tudo abaixo é derivado do CTA por color-mix com
// --surface, então inverte sozinho em tema de superfície escura (Barbearia/Tatuagem).
const CTA_SOFT = `color-mix(in srgb, ${CTA_FILL} 12%, var(--surface))`;
const CTA_SOFT_STRONG = `color-mix(in srgb, ${CTA_FILL} 20%, var(--surface))`;
const CTA_EDGE = `color-mix(in srgb, ${CTA_FILL} 34%, var(--surface))`;

const ACTION_BTN_SX = {
  bgcolor: CTA_FILL,
  color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&.Mui-disabled': {
    // Desabilitado precisa parecer DELIBERADO ("falta escolher"), não um botão quebrado.
    // Só neutros TEMÁVEIS: --surface/--divider/--text-secondary. A escala --tint-neutral-* é
    // global FIXA e clara — usá-la aqui dá pastilha clara + --text-secondary claro nos temas de
    // superfície escura (Barbearia, Tatuagem): 2,49:1. Com --surface a pastilha inverte junto
    // com o tema. Fica em --surface e não em --bg-page porque o próprio painel do CTA já é
    // --bg-page — senão o botão desaparece no fundo.
    bgcolor: 'var(--surface)',
    color: 'var(--text-secondary)',
    border: '1px dashed',
    borderColor: 'var(--divider)',
    boxShadow: 'none',
  },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

const OUTLINE_BTN_SX = {
  minHeight: 44, px: 2, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--divider)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', textTransform: 'none',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

const QUIET_BTN_SX = {
  minHeight: 44, px: 1.5, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 },
};

const DANGER_BTN_SX = {
  minHeight: 44, px: 2, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  bgcolor: 'var(--color-error)', color: 'var(--on-primary)', fontWeight: 'var(--fw-bold)',
  '&:hover': { bgcolor: 'var(--tint-error-fg)' },
  '&.Mui-disabled': { bgcolor: 'var(--tint-error-border)', color: 'var(--tint-error-fg)' },
  '&:focus-visible': { outline: '2px solid var(--color-error)', outlineOffset: 2 },
};

const RADIO_SX = {
  p: 0.75, color: 'var(--text-secondary)',
  '&.Mui-checked': { color: 'var(--color-primary)' },
};

// rádio da vitrine: marcação na cor de ação, não na cor de marca
const RADIO_CTA_SX = {
  p: 0.75, color: 'var(--text-secondary)',
  '&.Mui-checked': { color: 'var(--color-cta, var(--tint-success-fg))' },
};

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', bgcolor: 'var(--surface)',
    '& fieldset': { borderColor: 'var(--divider)' },
    '&:hover fieldset': { borderColor: 'var(--color-primary-light)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    '&.Mui-error fieldset': { borderColor: 'var(--color-error)' },
  },
};

const DIALOG_PAPER_SX = {
  bgcolor: 'var(--surface)', color: 'var(--text-primary)',
  borderRadius: 'var(--radius-card)', border: '1px solid', borderColor: 'var(--divider)',
};

// Título de diálogo é título de CARD, não de página: fonte de corpo, hierarquia por peso.
const DIALOG_TITLE_SX = {
  px: 3, pt: 3, pb: 1,
  fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-h6)', color: 'var(--text-primary)',
};

// Endereço: sempre pode quebrar (mockComNomeLongo tem domínios e títulos enormes).
const URL_SX = { overflowWrap: 'anywhere', lineHeight: 1.3 };

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

/** Painel interno do card (um bloco: pagamento, um domínio, a oferta). */
function Panel({ tone = 'neutral', children, sx }) {
  const t = TINT[tone] || TINT.neutral;
  return (
    <Box
      sx={{
        border: '1px solid', borderColor: t.border, bgcolor: t.bg,
        borderRadius: 'var(--radius-sm)', p: { xs: 1.75, sm: 2.25 },
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Chip de estado — DOIS REGISTROS, um critério: o estado PEDE AÇÃO ou não.
 *   · error / warning  → pastilha preenchida (tint + fio + ícone): salta numa tabela de 40 linhas;
 *   · success / info / neutral → PONTO + rótulo, sem pastilha: a coluna fica calma e quem aparece
 *     é o dado. Numa lista longa, 40 pastilhas verdes competem com o conteúdo e escondem as duas
 *     vermelhas — que são as únicas que mudam o que o dono do negócio vai fazer agora.
 *  Estado nunca só por cor: o rótulo nomeia o estado nos dois registros (e a pastilha leva ícone).
 *  Vale igual nos dois modos: o ponto usa o -fg do tint, que é medido em claro e em escuro. */
const CHIP_FILLED = { error: true, warning: true };

function ToneChip({ tone = 'neutral', icon, label }) {
  const t = TINT[tone] || TINT.neutral;
  if (!CHIP_FILLED[tone]) return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, maxWidth: '100%', flexShrink: 0, minWidth: 0 }}>
      <Box aria-hidden="true" component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.fg, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: tone === 'neutral' ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, maxWidth: '100%',
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 999, px: 1.125, py: '3px',
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: t.fg, fontWeight: 'var(--fw-medium)' }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Linha de aviso dentro de um bloco (tint + ícone + texto pronto do VM). */
function NoteRow({ tone = 'info', icon, children, sx }) {
  const t = TINT[tone] || TINT.info;
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25, ...sx,
      }}
    >
      {icon && <Box component="span" sx={{ display: 'flex', color: t.fg, mt: '1px', flexShrink: 0 }}>{icon}</Box>}
      <Box sx={{ minWidth: 0, color: t.fg }}>{children}</Box>
    </Box>
  );
}

function SectionTitle({ children, hint }) {
  return (
    <Box>
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>
        {children}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mt: '2px', lineHeight: 1.45 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** Bloco de estado do card inteiro (erro, convite a publicar página). */
function StateBlock({ icon, title, description, children, action }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      {children}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A VITRINE — o alarde da oferta.
   Este é o único lugar do Admin onde pedimos dinheiro por algo que a pessoa não sabia
   que queria: a troca "endereço genérico → endereço seu" tem que bater o olho e ser
   entendida antes de qualquer leitura. Daí a vitrine ser a maior coisa do card, com o
   endereço novo em escala de manchete.

   Movimento: entrada escalonada (rise) e um brilho lento no botão. Nada de contagem
   regressiva nem barra fingida — a urgência é o FATO de quem registra primeiro leva.
   Tudo desligado em prefers-reduced-motion.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Entrada escalonada: o olho segue a ordem da leitura em vez de receber tudo de uma vez. */
const rise = (delay = 0) => ({
  '@keyframes domRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'domRise .55s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

function OfferHero({ currentLabel, futureLabel, futureIsPlaceholder, price }) {
  return (
    <Box
      sx={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 'var(--radius-card)',
        bgcolor: CTA_SOFT,
        border: '1px solid', borderColor: CTA_EDGE,
        p: { xs: 2, sm: 3 },
        ...rise(0),
      }}
    >
      {/* marca d'água decorativa — puramente visual */}
      <LanguageRounded
        aria-hidden="true"
        sx={{
          position: 'absolute', right: -28, top: -28, fontSize: 200,
          color: CTA_FILL, opacity: 0.1, pointerEvents: 'none',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="overline" sx={{ display: 'block', color: CTA_FILL_DARK, fontWeight: 'var(--fw-bold)', letterSpacing: '.1em', lineHeight: 1.6 }}>
            Domínio próprio
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              fontWeight: 'var(--fw-bold)',
              color: 'var(--text-primary)', lineHeight: 1.15, mt: 0.5,
              fontSize: { xs: '1.375rem', sm: '1.75rem' }, textWrap: 'pretty',
              maxWidth: 560,
            }}
          >
            Pare de divulgar o nosso endereço. Divulgue o seu.
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', mt: 1, maxWidth: 520, lineHeight: 1.5 }}>
            A gente registra o nome, aponta para as suas páginas e deixa tudo no ar. Você não
            mexe em nada — e o endereço fica no seu nome.
          </Typography>
        </Box>

        {/* A TROCA, em escala de manchete */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...rise(90) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ ...URL_SX, color: 'var(--text-secondary)', textDecoration: 'line-through', textDecorationColor: 'var(--text-secondary)' }}
            >
              {currentLabel}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
            <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: 28, color: CTA_FILL, flexShrink: 0, mt: 0.25, display: { xs: 'none', sm: 'block' } }} />
            <ArrowDownwardRounded aria-hidden="true" sx={{ fontSize: 22, color: CTA_FILL, flexShrink: 0, mt: 0.25, display: { xs: 'block', sm: 'none' } }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                component="p"
                aria-live="polite"
                sx={{
                  ...URL_SX,
                  fontWeight: 'var(--fw-bold)',
                  fontSize: { xs: '1.5rem', sm: '2.125rem' },
                  color: futureIsPlaceholder ? 'var(--text-secondary)' : CTA_FILL_DARK,
                }}
              >
                {futureLabel}
              </Typography>
              {/* sublinhado que cresce: reforça o endereço novo sem piscar */}
              <Box
                aria-hidden="true"
                sx={{
                  height: 4, borderRadius: 999, mt: 0.5, bgcolor: CTA_FILL,
                  '@keyframes domGrow': { from: { transform: 'scaleX(0)' }, to: { transform: 'scaleX(1)' } },
                  transformOrigin: 'left',
                  animation: 'domGrow .6s cubic-bezier(.2,.7,.3,1) both',
                  animationDelay: '260ms',
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Preço: sempre do VM (muda sem redeploy) */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, ...rise(180) }}>
          <Typography
            variant="h4"
            component="p"
            sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', fontSize: { xs: '1.75rem', sm: '2rem' }, lineHeight: 1 }}
          >
            {price?.label}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
            {price?.periodLabel}
          </Typography>
          {price?.monthlyEquivalentLabel && (
            <Box sx={{ bgcolor: 'var(--surface)', border: '1px solid', borderColor: CTA_EDGE, borderRadius: 999, px: 1.25, py: '3px' }}>
              <Typography variant="caption" sx={{ color: CTA_FILL_DARK, fontWeight: 'var(--fw-bold)' }}>
                {price.monthlyEquivalentLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BLOCO C — A OFERTA
   ═══════════════════════════════════════════════════════════════════════════ */

const BENEFIT_ICON = {
  brand: StorefrontRounded,
  trust: VerifiedUserRounded,
  memory: RecordVoiceOverRounded,
  ownership: KeyRounded,
};

/**
 * As vantagens em cartões, não em linhas de rodapé: antes eram caption cinza e ninguém lia.
 * Cada um entra escalonado e levanta no hover — o texto vem do VM (`vm.benefits`).
 */
function Benefits({ benefits, compact }) {
  if (!benefits?.length) return null;
  return (
    <Box
      sx={{
        display: 'grid', gap: 1.25,
        gridTemplateColumns: compact
          ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
          : { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
      }}
    >
      {benefits.map((b, i) => {
        const Icon = BENEFIT_ICON[b.key] || CheckCircleRounded;
        return (
          <Box
            key={b.key}
            sx={{
              minWidth: 0, p: 1.5, borderRadius: 'var(--radius-sm)',
              bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
              transition: 'transform .18s ease, border-color .18s ease, box-shadow .18s ease',
              '&:hover': { transform: 'translateY(-2px)', borderColor: CTA_EDGE, boxShadow: 'var(--shadow-card)' },
              '@media (prefers-reduced-motion: reduce)': { '&:hover': { transform: 'none' } },
              ...rise(120 + i * 70),
            }}
          >
            <Box
              sx={{
                width: 34, height: 34, borderRadius: '50%', mb: 1,
                bgcolor: CTA_SOFT_STRONG,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon sx={{ fontSize: 20, color: CTA_FILL_DARK }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {b.title}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', lineHeight: 1.45, mt: 0.25, overflowWrap: 'anywhere' }}>
              {b.description}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * O nome recomendado (rank 1) em destaque — é o que a pessoa deve escolher se não quiser
 * pensar. Um <label> com Radio real: clique na área toda, foco visível e navegação por seta
 * dentro do grupo (mesmo `name` em todas as opções).
 */
function RecommendedOption({ suggestion, disabled, onSelect }) {
  const on = !!suggestion.selected;
  return (
    <Box
      component="label"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, cursor: disabled ? 'default' : 'pointer',
        p: { xs: 1.5, sm: 1.75 }, minHeight: 56,
        borderRadius: 'var(--radius-sm)',
        border: on ? '2px solid' : '1px solid',
        borderColor: on ? CTA_FILL : CTA_EDGE,
        bgcolor: on ? CTA_SOFT : 'var(--surface)',
        transition: 'border-color .15s, background-color .15s',
        '&:hover': disabled ? undefined : { bgcolor: CTA_SOFT, borderColor: CTA_FILL },
        '&:focus-within': { boxShadow: `0 0 0 3px ${CTA_EDGE}` },
      }}
    >
      <Radio
        name="dominio-sugerido"
        value={suggestion.domain}
        checked={on}
        disabled={disabled}
        onChange={() => onSelect(suggestion.domain)}
        inputProps={{ 'aria-label': `Escolher o domínio ${suggestion.domain}` }}
        sx={RADIO_CTA_SX}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="h6"
          component="p"
          sx={{ ...URL_SX, color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}
        >
          {suggestion.domain}
        </Typography>
        {suggestion.badgeLabel && (
          <Box sx={{ mt: 0.75 }}>
            <ToneChip tone="cta" icon={<BoltRounded sx={{ fontSize: 14 }} />} label={suggestion.badgeLabel} />
          </Box>
        )}
      </Box>
      {on && <CheckCircleRounded sx={{ fontSize: 22, color: CTA_FILL, flexShrink: 0 }} />}
    </Box>
  );
}

/** As alternativas: mesmo grupo de rádio, forma compacta que embrulha sem esticar o card. */
function AlternativeOption({ suggestion, disabled, onSelect }) {
  const on = !!suggestion.selected;
  return (
    <Box
      component="label"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%',
        cursor: disabled ? 'default' : 'pointer', pl: 0.5, pr: 1.5, py: 0.5, minHeight: 44,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid',
        borderColor: on ? CTA_FILL : 'var(--divider)',
        bgcolor: on ? CTA_SOFT : 'var(--surface)',
        transition: 'border-color .15s, background-color .15s',
        '&:hover': disabled ? undefined : { borderColor: CTA_FILL, bgcolor: CTA_SOFT },
        '&:focus-within': { boxShadow: `0 0 0 3px ${CTA_EDGE}` },
      }}
    >
      <Radio
        name="dominio-sugerido"
        value={suggestion.domain}
        checked={on}
        disabled={disabled}
        onChange={() => onSelect(suggestion.domain)}
        size="small"
        inputProps={{ 'aria-label': `Escolher o domínio ${suggestion.domain}` }}
        sx={RADIO_CTA_SX}
      />
      <Typography
        variant="body2"
        sx={{ ...URL_SX, color: on ? CTA_FILL_DARK : 'var(--text-primary)', fontWeight: on ? 'var(--fw-bold)' : 'var(--fw-medium)' }}
      >
        {suggestion.domain}
      </Typography>
    </Box>
  );
}

const CUSTOM_STATUS = {
  checking: { tone: 'neutral', Icon: null },
  available: { tone: 'success', Icon: CheckCircleRounded },
  taken: { tone: 'error', Icon: CancelRounded },
  invalid: { tone: 'error', Icon: CancelRounded },
  too_expensive: { tone: 'warning', Icon: WarningAmberRounded },
  throttled: { tone: 'warning', Icon: ScheduleRounded },
  error: { tone: 'error', Icon: ErrorRounded },
};

/**
 * "Quero outro nome". A checagem é ao vivo (debounce no container); a View só reporta o
 * texto e desenha o status. `message` vem pronto do VM em todos os status menos `checking`,
 * que é texto de vitrine (nosso).
 */
function CustomDomainBox({ custom, disabled, onType, onSelect }) {
  const c = custom || {};
  const status = c.status || 'idle';
  const cfg = CUSTOM_STATUS[status];
  const { Icon } = cfg || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          value={c.input || ''}
          onChange={(e) => onType(e.target.value)}
          placeholder="meunegocio.com"
          size="small"
          disabled={disabled}
          error={status === 'taken' || status === 'invalid' || status === 'error'}
          inputProps={{ 'aria-label': 'Digitar outro nome de domínio', autoCapitalize: 'none', spellCheck: 'false' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {status === 'checking' ? (
                  <CircularProgress size={16} aria-label="Conferindo o nome" sx={{ color: 'var(--color-primary)' }} />
                ) : Icon ? (
                  <Icon sx={{ fontSize: 20, color: TINT[cfg.tone].main }} />
                ) : null}
              </InputAdornment>
            ),
          }}
          sx={{ ...FIELD_SX, flex: '1 1 240px', minWidth: 0 }}
        />
        {c.canUse && !c.selected && (
          <Button onClick={() => onSelect(c.input)} disabled={disabled} sx={{ ...OUTLINE_BTN_SX, borderColor: CTA_FILL, color: CTA_FILL_DARK, '&:hover': { borderColor: CTA_FILL, color: CTA_FILL_DARK, bgcolor: CTA_SOFT } }}>
            Usar este nome
          </Button>
        )}
        {c.selected && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
            <ToneChip tone="success" icon={<CheckCircleRounded sx={{ fontSize: 14 }} />} label="Nome escolhido" />
          </Box>
        )}
      </Box>

      <Typography
        variant="caption"
        role="status"
        sx={{
          color: status === 'idle' ? 'var(--text-secondary)' : TINT[cfg.tone].fg,
          fontWeight: status === 'idle' ? 'var(--fw-regular)' : 'var(--fw-medium)',
          lineHeight: 1.45,
        }}
      >
        {status === 'checking' ? 'Conferindo se este nome está livre…' : c.message || c.helperLabel}
      </Typography>
    </Box>
  );
}

/**
 * Qual página responde na raiz. O conceito que mais confunde, então cada linha mostra a troca
 * concreta daquela página: endereço de hoje → endereço com o domínio novo (futureUrlLabel).
 * `accent` liga o tom da vitrine (cor de ação) — na oferta, onde a peça está vendendo. No
 * diálogo de trocar a raiz de um domínio já comprado não se vende nada, então lá fica a cor
 * de marca.
 */
function RootPagePicker({ pages, selectedPageId, name, disabled, onPick, accent }) {
  const mark = accent ? CTA_FILL : 'var(--color-primary)';
  const soft = accent ? CTA_SOFT : 'var(--color-primary-soft)';
  const edge = accent ? CTA_EDGE : 'var(--color-primary-light)';
  const ink = accent ? CTA_FILL_DARK : 'var(--color-primary-dark)';
  return (
    <Box
      role="radiogroup"
      aria-label="Página que responde no endereço principal"
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
    >
      {(pages || []).map((p) => {
        const on = selectedPageId ? p.pageId === selectedPageId : !!p.isRoot;
        return (
          <Box
            key={p.pageId}
            component="label"
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1, minHeight: 56,
              cursor: disabled ? 'default' : 'pointer', p: 1.25,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid',
              borderColor: on ? mark : 'var(--divider)',
              bgcolor: on ? soft : 'var(--surface)',
              transition: 'border-color .15s, background-color .15s',
              '&:hover': disabled ? undefined : { borderColor: mark },
              '&:focus-within': { boxShadow: `0 0 0 3px ${edge}` },
            }}
          >
            <Radio
              name={name}
              value={p.pageId}
              checked={on}
              disabled={disabled}
              onChange={() => onPick(p.pageId)}
              inputProps={{ 'aria-label': `Deixar ${p.title} no endereço principal` }}
              sx={{ ...(accent ? RADIO_CTA_SX : RADIO_SX), mt: '-2px' }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                  {p.title}
                </Typography>
                {on && <ToneChip tone={accent ? 'cta' : 'brand'} icon={<HomeRounded sx={{ fontSize: 14 }} />} label="Endereço principal" />}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>
                {p.typeLabel}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                <Typography variant="caption" sx={{ ...URL_SX, color: 'var(--text-secondary)' }}>
                  {p.currentUrlLabel}
                </Typography>
                <ArrowForwardRounded sx={{ fontSize: 14, color: 'var(--text-secondary)' }} aria-hidden="true" />
                <Typography variant="caption" sx={{ ...URL_SX, color: ink, fontWeight: 'var(--fw-bold)' }}>
                  {p.futureUrlLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function OfferBlock({ vm, offer, a }) {
  const disabled = !!vm.busy;
  const suggestions = offer.suggestions || [];
  const recommended = suggestions.find((s) => s.recommended) || suggestions[0] || null;
  const alternatives = suggestions.filter((s) => s !== recommended);
  const pages = offer.pages || [];
  const rootPage = pages.find((p) => p.pageId === offer.rootPageId) || pages.find((p) => p.isRoot) || pages[0] || null;

  return (
    <Box
      sx={{
        display: 'grid', alignItems: 'start', gap: 'var(--space-2)',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
      }}
    >
      {rootPage && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <OfferHero
            currentLabel={rootPage.currentUrlLabel}
            futureLabel={offer.selectedDomain || rootPage.futureUrlLabel}
            futureIsPlaceholder={!offer.selectedDomain}
            price={vm.price}
          />
        </Box>
      )}

      {/* Urgência honesta: é FATO (quem registra primeiro leva), não escassez inventada.
          Sem contador, sem "últimas vagas". */}
      <Box
        sx={{
          gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1.25,
          bgcolor: 'var(--tint-warning-bg)', border: '1px solid', borderColor: 'var(--tint-warning-border)',
          borderRadius: 'var(--radius-sm)', px: { xs: 1.5, sm: 2 }, py: 1.5,
          ...rise(60),
        }}
      >
        <BoltRounded
          sx={{
            fontSize: 26, color: 'var(--tint-warning-fg)', flexShrink: 0,
            '@keyframes domPulse': { '0%, 100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.12)' } },
            animation: 'domPulse 2.6s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: 'var(--tint-warning-fg)', fontWeight: 'var(--fw-bold)', lineHeight: 1.35 }}>
            Estes nomes estão livres agora — e domínio é de quem registra primeiro.
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--tint-warning-fg)', lineHeight: 1.45 }}>
            Amanhã o nome do seu negócio pode estar no nome de outra pessoa. Não dá para reservar.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ gridColumn: '1 / -1' }}>
        <Benefits benefits={vm.benefits} />
      </Box>

      {/* 1. Escolher o nome */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
        <SectionTitle hint={offer.suggestionsEmpty ? null : 'O primeiro é o mais parecido com o nome do seu negócio.'}>
          Escolha o seu nome
        </SectionTitle>

        {offer.suggestionsEmpty ? (
          <NoteRow tone="neutral" icon={<LanguageRounded sx={{ fontSize: 18 }} />}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{offer.suggestionsEmptyLabel}</Typography>
          </NoteRow>
        ) : (
          <Box role="radiogroup" aria-label="Nomes de domínio disponíveis" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {recommended && (
              <RecommendedOption suggestion={recommended} disabled={disabled} onSelect={(d) => a.onSelectDomain?.(d)} />
            )}
            {alternatives.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {alternatives.map((s) => (
                  <AlternativeOption key={s.domain} suggestion={s} disabled={disabled} onSelect={(d) => a.onSelectDomain?.(d)} />
                ))}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ pt: 0.5 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mb: 0.75 }}>
            Prefere outro nome?
          </Typography>
          <CustomDomainBox
            custom={offer.custom}
            disabled={disabled}
            onType={(t) => a.onTypeCustomDomain?.(t)}
            onSelect={(d) => a.onSelectDomain?.(d)}
          />
        </Box>
      </Box>

      {/* 2. Qual página fica na raiz */}
      {offer.showRootPicker && pages.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
          <SectionTitle hint="Uma página responde no endereço curto; as outras ganham um complemento.">
            Qual página fica no endereço principal
          </SectionTitle>
          <RootPagePicker
            pages={pages}
            selectedPageId={offer.rootPageId}
            name="raiz-oferta"
            disabled={disabled}
            onPick={(id) => a.onSelectRootPage?.(id)}
            accent
          />
        </Box>
      )}

      {/* 3. Comprar — a ação mais visível do card: botão grande, largura cheia no celular,
          com um brilho lento (não pisca, não some) e a linha de segurança logo abaixo. */}
      <Box
        sx={{
          gridColumn: '1 / -1', mt: 'var(--space-1)',
          p: { xs: 1.75, sm: 2.25 }, borderRadius: 'var(--radius-card)',
          bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
          display: 'flex', flexDirection: 'column', gap: 1.5,
          ...rise(240),
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
          <Button
            onClick={() => a.onStartPurchase?.()}
            disabled={!offer.canPurchase || disabled}
            disableElevation
            startIcon={vm.busy ? <CircularProgress size={18} sx={{ color: 'var(--on-primary)' }} /> : <BoltRounded sx={{ fontSize: 22, opacity: offer.canPurchase ? 1 : 0.6 }} />}
            sx={{
              ...ACTION_BTN_SX,
              width: { xs: '100%', sm: 'auto' },
              minHeight: 54, px: 3, fontSize: '1rem',
              position: 'relative', overflow: 'hidden',
              boxShadow: 'var(--shadow-card)',
              transition: 'transform .18s ease',
              '&:hover': { bgcolor: CTA_FILL_DARK, transform: 'translateY(-1px)' },
              '&::after': offer.canPurchase && !disabled ? {
                content: '""', position: 'absolute', top: 0, bottom: 0, width: '45%', left: '-60%',
                background: 'linear-gradient(90deg, transparent, color-mix(in srgb, var(--on-primary) 30%, transparent), transparent)',
                '@keyframes domSheen': { '0%': { left: '-60%' }, '55%, 100%': { left: '120%' } },
                animation: 'domSheen 3.6s ease-in-out infinite',
                pointerEvents: 'none',
              } : undefined,
              '@media (prefers-reduced-motion: reduce)': {
                '&:hover': { transform: 'none' },
                '&::after': { animation: 'none', display: 'none' },
              },
            }}
          >
            {vm.busy ? 'Gerando o código PIX…' : offer.ctaLabel}
          </Button>
          {vm.dismissible && (
            <Button onClick={() => a.onDismiss?.()} disabled={disabled} sx={QUIET_BTN_SX}>
              Agora não
            </Button>
          )}
        </Box>

        {/* três garantias curtas — o que costuma travar a compra: "e se eu não souber mexer?" */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 2 } }}>
          {['Registro no seu nome', 'A configuração é por nossa conta', 'Pagamento por PIX, na hora'].map((label) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleRounded sx={{ fontSize: 16, color: 'var(--color-success)' }} />
              <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          {!offer.canPurchase && !offer.selectedDomain
            ? 'Escolha um nome acima para continuar.'
            : vm.price?.renewalNoticeLabel}
        </Typography>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BLOCO B — O PAGAMENTO EM ABERTO
   O bloco que a v2 não tinha. Ele é RECUPERADO do servidor: a leitura tem que ser
   "sua cobrança está aqui e continua aqui, de qualquer aparelho, até você pagar ou desistir".
   ═══════════════════════════════════════════════════════════════════════════ */

const PAYMENT_TONE = { normal: 'info', soon: 'warning', expired: 'error' };

function PaymentBlock({ vm, payment, a }) {
  const tone = PAYMENT_TONE[payment.urgency] || 'info';
  const t = TINT[tone];
  const blocked = payment.expired || payment.unavailable;
  const canCancel = payment.canCancel && !vm.busy;

  const cancelButton = (
    <Button
      onClick={() => a.onOpenCancelDialog?.()}
      disabled={!canCancel}
      sx={blocked ? { ...OUTLINE_BTN_SX, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)' } : QUIET_BTN_SX}
    >
      {blocked ? 'Desistir e escolher outro nome' : 'Desistir desta compra'}
    </Button>
  );

  return (
    <Panel tone={tone}>
      {/* Cabeçalho da cobrança: o que está sendo comprado, por quanto, e para onde vai */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ display: 'block', color: t.sub, letterSpacing: '.06em', fontWeight: 'var(--fw-bold)', lineHeight: 1.6 }}>
            Cobrança em aberto
          </Typography>
          <Typography variant="h6" component="p" sx={{ ...URL_SX, color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>
            {payment.domain}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25 }}>
            {payment.priceLabel} · {payment.startedLabel}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.75 }}>
          <ToneChip
            tone={tone}
            icon={payment.expired ? <TimerOffRounded sx={{ fontSize: 14 }} /> : <ScheduleRounded sx={{ fontSize: 14 }} />}
            label={payment.expiresLabel}
          />
          {/* Poll conferindo o pagamento: sempre no fluxo, só muda de opacidade — acende a cada
              5s e não pode piscar nem empurrar o layout. */}
          <Box
            aria-live="polite"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 20,
              opacity: payment.checking ? 1 : 0, transition: 'opacity .4s ease',
            }}
          >
            <CircularProgress size={12} sx={{ color: 'var(--text-secondary)' }} />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              {payment.checking ? 'Conferindo se o pagamento caiu…' : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <HomeRounded sx={{ fontSize: 16, color: 'var(--text-secondary)' }} aria-hidden="true" />
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
          Endereço principal: <strong>{payment.rootPageLabel}</strong>
        </Typography>
      </Box>

      {payment.expired ? (
        <NoteRow tone="error" icon={<TimerOffRounded sx={{ fontSize: 20 }} />}>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            <strong>Esta cobrança venceu</strong> — nada foi cobrado. Desista dela e escolha o nome de
            novo; o código sai na hora.
          </Typography>
        </NoteRow>
      ) : payment.unavailable ? (
        <NoteRow tone="warning" icon={<CloudOffRounded sx={{ fontSize: 20 }} />}>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            <strong>Não conseguimos recuperar o código desta cobrança.</strong> Se ainda não pagou,
            desista dela e faça de novo. Se já pagou, fale com o suporte antes de cancelar.
          </Typography>
        </NoteRow>
      ) : (
        <>
          {/* QR + copia-e-cola. No celular o QR vem primeiro e grande; o botão de copiar é o
              alvo de dedo (46px) porque é o caminho mais usado em pagamento no próprio aparelho. */}
          <Box
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 'var(--space-2)', sm: 'var(--space-3)' }, alignItems: { sm: 'flex-start' },
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', sm: 200 }, maxWidth: 240, flexShrink: 0,
                alignSelf: { xs: 'center', sm: 'flex-start' },
                aspectRatio: '1 / 1', p: 1,
                bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
                borderRadius: 'var(--radius-sm)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
              }}
            >
              {payment.qrCodeImage ? (
                <Box
                  component="img"
                  src={payment.qrCodeImage}
                  alt={`QR Code do PIX para pagar o registro de ${payment.domain}`}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain', imageRendering: 'pixelated' }}
                />
              ) : (
                <QrCode2Rounded sx={{ fontSize: 56, color: 'var(--text-secondary)' }} />
              )}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
                No banco, escolha pagar com PIX e aponte a câmera. No celular, copie e cole o código.
              </Typography>

              <Box
                sx={{
                  p: 1.25, borderRadius: 'var(--radius-sm)',
                  bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
                }}
              >
                <Typography
                  variant="caption"
                  component="p"
                  sx={{
                    color: 'var(--text-secondary)', overflowWrap: 'anywhere', lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}
                >
                  {payment.copyPasteCode}
                </Typography>
              </Box>

              <Box>
                <Button
                  onClick={() => a.onCopyPixCode?.()}
                  disabled={!payment.copyPasteCode}
                  disableElevation
                  startIcon={payment.copied ? <DoneRounded sx={{ fontSize: 20 }} /> : <ContentCopyRounded sx={{ fontSize: 18 }} />}
                  sx={payment.copied
                    ? { ...ACTION_BTN_SX, bgcolor: CTA_FILL_DARK, '&:hover': { bgcolor: CTA_FILL_DARK } }
                    : ACTION_BTN_SX}
                >
                  {payment.copied ? 'Código copiado' : 'Copiar código PIX'}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* A tranquilidade é o coração deste bloco: pode fechar a tela. */}
          <NoteRow tone="success" icon={<DevicesRounded sx={{ fontSize: 20 }} />}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
              <strong>{payment.reassuranceLabel}</strong> {payment.nextStepLabel}
            </Typography>
          </NoteRow>
        </>
      )}

      {/* Desistir: encontrável, sem ser convite. A ausência desta saída foi o que trancou o
          cliente na primeira compra real. */}
      <Box
        sx={{
          pt: 1.25, borderTop: '1px solid', borderColor: t.border,
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between',
        }}
      >
        {cancelButton}
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', maxWidth: 320, lineHeight: 1.45 }}>
          {blocked ? 'Nada é cobrado ao desistir.' : 'Já pagou? A confirmação do banco leva alguns minutos.'}
        </Typography>
      </Box>
    </Panel>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   BLOCO A — OS DOMÍNIOS DA CONTA
   A pergunta é sempre "já está no ar?". Quando não está: onde estamos, e se está saudável.
   ═══════════════════════════════════════════════════════════════════════════ */

const STEP_TONE = {
  registering: 'info',
  pointing: 'info',
  securing: 'info',
  active: 'success',
  failed: 'error',
  expired: 'error',
};

const STEP_ICON = {
  registering: HourglassTopRounded,
  pointing: HourglassTopRounded,
  securing: HourglassTopRounded,
  active: CheckCircleRounded,
  failed: ErrorRounded,
  expired: TimerOffRounded,
};

const TIMELINE_STATE = {
  done: { label: 'concluído', color: 'var(--color-success)' },
  current: { label: 'em andamento', color: 'var(--color-primary)' },
  todo: { label: 'aguardando', color: 'var(--text-secondary)' },
  failed: { label: 'falhou', color: 'var(--color-error)' },
};

/**
 * A linha do tempo do apontamento: quatro passos reais do DNS. É <ol> com o estado escrito em
 * cada item — em leitor de tela a lista faz sentido sozinha, e o estado nunca é só cor.
 * O passo corrente tem um ponto que respira: sinal de vida, não de progresso (a espera é
 * irregular e pode levar horas — animar como contagem seria mentir).
 */
function Timeline({ steps }) {
  if (!steps?.length) return null;
  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none', m: 0, p: 0, display: 'grid', gap: { xs: 1, sm: 1.5 },
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' },
      }}
    >
      {steps.map((s) => {
        const st = TIMELINE_STATE[s.state] || TIMELINE_STATE.todo;
        return (
          <Box component="li" key={s.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
            <Box sx={{ width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', mt: '1px' }}>
              {s.state === 'done' && <CheckCircleRounded sx={{ fontSize: 20, color: st.color }} />}
              {s.state === 'failed' && <ErrorRounded sx={{ fontSize: 20, color: st.color }} />}
              {s.state === 'todo' && <RadioButtonUncheckedRounded sx={{ fontSize: 20, color: st.color, opacity: 0.5 }} />}
              {s.state === 'current' && (
                <Box
                  sx={{
                    width: 12, height: 12, borderRadius: '50%', bgcolor: st.color,
                    boxShadow: '0 0 0 4px var(--color-primary-soft)',
                    '@keyframes domainBreathe': {
                      '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                      '50%': { transform: 'scale(.7)', opacity: 0.65 },
                    },
                    animation: 'domainBreathe 2.4s ease-in-out infinite',
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  color: s.state === 'todo' ? 'var(--text-secondary)' : 'var(--text-primary)',
                  fontWeight: s.state === 'current' ? 'var(--fw-bold)' : 'var(--fw-regular)',
                  lineHeight: 1.4,
                }}
              >
                {s.label}
              </Typography>
              <Typography variant="caption" sx={{ color: s.state === 'failed' ? 'var(--tint-error-fg)' : 'var(--text-secondary)' }}>
                {st.label}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/** Posição no processo — trilho ESTÁTICO, sem transição: não é tempo decorrido. */
function ProgressRail({ percent, label }) {
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{ height: 6, borderRadius: 999, bgcolor: 'var(--divider)', overflow: 'hidden' }}
    >
      <Box sx={{ width: `${Math.max(0, Math.min(100, percent || 0))}%`, height: '100%', bgcolor: 'var(--color-primary)', borderRadius: 999 }} />
    </Box>
  );
}

/** Um endereço do domínio: link quando está no ar, texto quando ainda não, sempre copiável. */
function UrlRow({ url, disabled, onCopy }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, minHeight: 44,
        px: 1.25, py: 0.75, borderRadius: 'var(--radius-sm)',
        bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)',
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {url.href ? (
            <Typography
              component="a"
              href={url.href}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{
                ...URL_SX, color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)',
                textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
              }}
            >
              {url.label}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ ...URL_SX, color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
              {url.label}
            </Typography>
          )}
          {url.isRoot && <ToneChip tone="brand" icon={<HomeRounded sx={{ fontSize: 14 }} />} label="Principal" />}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
          {url.pageTitle}
        </Typography>
      </Box>

      {url.copied ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, flexShrink: 0 }} role="status">
          <DoneRounded sx={{ fontSize: 18, color: 'var(--color-success)' }} />
          <Typography variant="caption" sx={{ color: 'var(--tint-success-fg)', fontWeight: 'var(--fw-medium)' }}>
            Copiado
          </Typography>
        </Box>
      ) : (
        <Tooltip title="Copiar endereço">
          <IconButton
            aria-label={`Copiar o endereço ${url.label}`}
            onClick={() => onCopy(url.label)}
            disabled={disabled}
            sx={{
              width: 40, height: 40, flexShrink: 0, color: 'var(--text-secondary)',
              '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
            }}
          >
            <ContentCopyRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function RenewalRow({ renewal }) {
  if (!renewal) return null;
  if (renewal.urgency === 'ok') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <EventRepeatRounded sx={{ fontSize: 16, color: 'var(--text-secondary)' }} aria-hidden="true" />
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
          {renewal.label} · {renewal.detailLabel}
        </Typography>
      </Box>
    );
  }
  const tone = renewal.urgency === 'expired' ? 'error' : 'warning';
  return (
    <NoteRow tone={tone} icon={<EventRepeatRounded sx={{ fontSize: 18 }} />}>
      <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', lineHeight: 1.4 }}>
        {renewal.label} · {renewal.detailLabel}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, lineHeight: 1.45 }}>
        {renewal.urgency === 'expired'
          ? 'Suas páginas seguem no ar no endereço do Agendário.'
          : 'Se não renovar, o endereço sai do ar.'}
      </Typography>
    </NoteRow>
  );
}

function DomainPanel({ vm, domain, a }) {
  const inProgress = domain.step === 'registering' || domain.step === 'pointing' || domain.step === 'securing';
  const tone = domain.stalled ? 'warning' : STEP_TONE[domain.step] || 'neutral';
  const StepIcon = STEP_ICON[domain.step] || LanguageRounded;
  const t = TINT[tone];

  return (
    <Panel tone={domain.step === 'active' ? 'neutral' : tone} sx={{ gap: 1.75 }}>
      {/* Identidade + estado */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box
            sx={{
              width: 40, height: 40, flexShrink: 0, borderRadius: 'var(--radius-sm)',
              bgcolor: domain.step === 'active' ? 'var(--tint-success-bg)' : t.bg,
              border: '1px solid', borderColor: domain.step === 'active' ? 'var(--tint-success-border)' : t.border,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {domain.step === 'active'
              ? <CelebrationRounded sx={{ fontSize: 22, color: 'var(--tint-success-fg)' }} />
              : <StepIcon sx={{ fontSize: 22, color: t.fg }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="h3" sx={{ ...URL_SX, color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>
              {domain.domain}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <ToneChip
                tone={domain.step === 'active' ? 'success' : tone}
                icon={<StepIcon sx={{ fontSize: 14 }} />}
                label={domain.stepLabel}
              />
              {domain.etaLabel && (
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                  {domain.etaLabel}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {domain.step === 'active' && domain.liveUrl && (
          <Button
            component="a"
            href={domain.liveUrl}
            target="_blank"
            rel="noopener"
            endIcon={<OpenInNewRounded sx={{ fontSize: 18 }} />}
            disableElevation
            sx={PRIMARY_BTN_SX}
          >
            Abrir meu site
          </Button>
        )}
      </Box>

      {/* Espera: trilho estático de posição + os quatro passos reais, e a verdade sobre o tempo */}
      {(inProgress || domain.step === 'failed') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <ProgressRail percent={domain.progressPercent} label={`Posição no processo: ${domain.stepLabel}`} />
          <Timeline steps={domain.timeline} />
          {inProgress && !domain.stalled && (
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Não dá para acelerar. Pode fechar a tela — avisamos quando estiver no ar.
            </Typography>
          )}
        </Box>
      )}

      {domain.stalled && domain.stalledLabel && (
        <NoteRow tone="warning" icon={<WarningAmberRounded sx={{ fontSize: 20 }} />}>
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            <strong>{domain.stalledLabel}</strong> — não precisa abrir chamado, a gente procura você.
          </Typography>
        </NoteRow>
      )}

      {/* Falhou DEPOIS de pago: a tela não encolhe, e diz o que fazer. */}
      {domain.step === 'failed' && (
        <NoteRow tone="error" icon={<ErrorRounded sx={{ fontSize: 20 }} />}>
          <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', lineHeight: 1.4 }}>
            {domain.errorMessage}
          </Typography>
          {domain.supportHint && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <SupportAgentRounded sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{domain.supportHint}</Typography>
            </Box>
          )}
        </NoteRow>
      )}

      {/* Os endereços — o que a pessoa cola no Instagram, no WhatsApp, no cartão */}
      {domain.urls?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <SectionTitle hint={domain.step === 'active' ? null : 'Respondem quando o apontamento terminar.'}>
            Seus endereços
          </SectionTitle>
          {domain.urls.map((u) => (
            <UrlRow key={u.label} url={u} disabled={!!vm.busy} onCopy={(label) => a.onCopyUrl?.(label)} />
          ))}
        </Box>
      )}

      {/* Rodapé do domínio: raiz atual (com troca), renovação e a papelada */}
      <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <HomeRounded sx={{ fontSize: 16, color: 'var(--text-secondary)' }} aria-hidden="true" />
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
              No endereço principal: <strong>{domain.rootPageTitle}</strong>
              {[domain.registeredAtLabel, domain.pricePaidLabel].filter(Boolean).map((s) => ` · ${s}`).join('')}
            </Typography>
          </Box>
          {domain.canChangeRootPage && (
            <Button
              onClick={() => a.onOpenRootPageDialog?.(domain.id)}
              disabled={!!vm.busy}
              startIcon={<SwapHorizRounded sx={{ fontSize: 18 }} />}
              sx={{ ...QUIET_BTN_SX, color: 'var(--color-primary-dark)', '&:hover': { bgcolor: 'var(--color-primary-soft)' } }}
            >
              Trocar página
            </Button>
          )}
        </Box>

        <RenewalRow renewal={domain.renewal} />
      </Box>
    </Panel>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   OS DIÁLOGOS (desistir e página raiz; o de pagamento é do container)
   ═══════════════════════════════════════════════════════════════════════════ */

function CancelDialog({ dialog, a }) {
  return (
    <Dialog
      open={!!dialog?.open}
      onClose={() => { if (!dialog?.busy) a.onCloseCancelDialog?.(); }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="domain-cancel-title"
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle id="domain-cancel-title" sx={DIALOG_TITLE_SX}>Desistir desta compra?</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          A cobrança de <strong>{dialog?.domain}</strong> será cancelada e o código do PIX deixa de
          valer. Dá para comprar de novo depois, mas o nome pode não estar mais livre.
        </Typography>
        {dialog?.warningLabel && (
          <NoteRow tone="warning" icon={<WarningAmberRounded sx={{ fontSize: 18 }} />} sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{dialog.warningLabel}</Typography>
          </NoteRow>
        )}
        {dialog?.error && (
          <NoteRow tone="error" icon={<ErrorRounded sx={{ fontSize: 18 }} />} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{dialog.error}</Typography>
          </NoteRow>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={() => a.onCloseCancelDialog?.()} disabled={!!dialog?.busy} sx={{ ...OUTLINE_BTN_SX, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)' }}>
          Manter a cobrança
        </Button>
        <Button
          onClick={() => a.onConfirmCancel?.()}
          disabled={!!dialog?.busy}
          disableElevation
          startIcon={dialog?.busy ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
          sx={DANGER_BTN_SX}
        >
          {dialog?.busy ? 'Cancelando…' : 'Desistir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RootPageDialog({ dialog, a }) {
  return (
    <Dialog
      open={!!dialog?.open}
      onClose={() => { if (!dialog?.busy) a.onCloseRootPageDialog?.(); }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="domain-root-title"
      PaperProps={{ sx: DIALOG_PAPER_SX }}
    >
      <DialogTitle id="domain-root-title" sx={DIALOG_TITLE_SX}>Quem responde no endereço principal</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          A página escolhida atende em <strong>{dialog?.domain}</strong>. As outras continuam no ar,
          com um complemento no endereço.
        </Typography>
        <RootPagePicker
          pages={dialog?.pages}
          selectedPageId={dialog?.selectedPageId}
          name="raiz-dialogo"
          disabled={!!dialog?.busy}
          onPick={(id) => a.onPickRootPage?.(id)}
        />
        {dialog?.error && (
          <NoteRow tone="error" icon={<ErrorRounded sx={{ fontSize: 18 }} />} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{dialog.error}</Typography>
          </NoteRow>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={() => a.onCloseRootPageDialog?.()} disabled={!!dialog?.busy} sx={QUIET_BTN_SX}>
          Cancelar
        </Button>
        <Button
          onClick={() => a.onConfirmRootPage?.()}
          disabled={!dialog?.canConfirm || !!dialog?.busy}
          disableElevation
          startIcon={dialog?.busy ? <CircularProgress size={16} sx={{ color: 'var(--on-primary)' }} /> : null}
          sx={PRIMARY_BTN_SX}
        >
          {dialog?.busy ? 'Trocando…' : 'Trocar endereço principal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Cabeçalho do card + tarja de feedback da última ação
   ═══════════════════════════════════════════════════════════════════════════ */

function CardHead({ vm, a, showUpdated, selling }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Box
        sx={{
          width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--radius-sm)',
          // `selling`: nos estados de venda a pastilha entra na cor de ação, senão ela fica sendo
          // a única nota da cor de marca no topo da peça — e é onde o olho bate primeiro.
          // Em owned / awaiting_payment / needs_page não se vende nada: lá fica a cor de marca.
          bgcolor: selling ? CTA_SOFT_STRONG : 'var(--color-primary-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <LanguageRounded sx={{ fontSize: 24, color: selling ? CTA_FILL_DARK : 'var(--color-primary-dark)' }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', lineHeight: 1.3 }}
        >
          {vm.headline?.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: '2px', lineHeight: 1.45 }}>
          {vm.headline?.subtitle}
        </Typography>
      </Box>

      {showUpdated && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {/* refreshing é o poll de 5s: indicador discreto, nunca spinner de tela. */}
          <Typography
            variant="caption"
            sx={{ display: { xs: 'none', sm: 'block' }, color: 'var(--text-secondary)', opacity: vm.refreshing ? 0.6 : 1 }}
          >
            {vm.lastUpdatedLabel}
          </Typography>
          <Tooltip title="Atualizar">
            <IconButton
              aria-label="Atualizar o estado dos domínios"
              onClick={() => a.onRefresh?.()}
              disabled={!!vm.refreshing}
              sx={{
                width: 40, height: 40, color: 'var(--text-secondary)',
                '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
                '&.Mui-disabled': { color: 'var(--text-secondary)', opacity: 0.5 },
              }}
            >
              {vm.refreshing
                ? <CircularProgress size={16} sx={{ color: 'var(--text-secondary)' }} />
                : <RefreshRounded sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

const FEEDBACK_TONE = { error: 'error', success: 'success', info: 'info' };
const FEEDBACK_ICON = { error: ErrorRounded, success: CheckCircleRounded, info: BoltRounded };

function FeedbackBar({ feedback, busy, a }) {
  if (!feedback) return null;
  const tone = FEEDBACK_TONE[feedback.severity] || 'info';
  const Icon = FEEDBACK_ICON[feedback.severity] || BoltRounded;
  return (
    <NoteRow tone={tone} icon={<Icon sx={{ fontSize: 20 }} />}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ lineHeight: 1.45 }} role="status">
          {feedback.message}
        </Typography>
        {feedback.retryable && (
          <Button
            onClick={() => a.onRefresh?.()}
            disabled={!!busy}
            startIcon={<ReplayRounded sx={{ fontSize: 18 }} />}
            sx={{ ...OUTLINE_BTN_SX, minHeight: 40, bgcolor: 'var(--surface)' }}
          >
            Tentar de novo
          </Button>
        )}
      </Box>
    </NoteRow>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function DomainsView({ vm, actions }) {
  const a = actions || {};
  // Único estado de UI: em `owned` a oferta de um segundo domínio começa recolhida — senao o
  // card viraria uma fita (domínio + linha do tempo + oferta inteira na mesma tela).
  const [showOffer, setShowOffer] = useState(false);

  // `hidden` (venda não liberada) e `empty` (produto fora do catálogo): o card não existe.
  if (!vm || vm.state === 'hidden' || vm.state === 'empty') return null;

  const { state } = vm;
  const shell = (children) => (
    <Box
      component="section"
      aria-label="Domínio próprio"
      sx={{
        ...CARD_SX,
        width: '100%', maxWidth: { xs: '100%', md: 1120 }, mx: 'auto',
        p: { xs: 2, sm: 2.5, md: 3 },
        color: 'var(--text-primary)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
      }}
    >
      {children}
    </Box>
  );

  /* ── loading: só o esqueleto (o poll não volta aqui) ─────────────────── */
  if (state === 'loading') {
    return shell(
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }} aria-busy="true" aria-label="Carregando seus domínios">
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
          <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: 'var(--radius-sm)' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="45%" sx={{ fontSize: 'var(--fs-subtitle1)' }} />
            <Skeleton variant="text" width="70%" sx={{ fontSize: 'var(--fs-body2)' }} />
          </Box>
        </Box>
        <Skeleton variant="rounded" height={84} sx={{ borderRadius: 'var(--radius-sm)' }} />
        <Skeleton variant="rounded" height={64} sx={{ borderRadius: 'var(--radius-sm)' }} />
        <Skeleton variant="rounded" height={46} width={220} sx={{ borderRadius: 'var(--radius-sm)' }} />
      </Box>
    );
  }

  /* ── error: falhou carregar ──────────────────────────────────────────── */
  if (state === 'error') {
    return shell(
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar seus domínios"
        description={vm.errorMessage}
        action={(
          <Button onClick={() => a.onRefresh?.()} startIcon={<ReplayRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>
            Tentar novamente
          </Button>
        )}
      />
    );
  }

  /* ── needs_page: oportunidade, não erro ──────────────────────────────── */
  if (state === 'needs_page') {
    return shell(
      <>
        <CardHead vm={vm} a={a} showUpdated={false} />
        <FeedbackBar feedback={vm.actionFeedback} busy={vm.busy} a={a} />
        <Panel tone="brand" sx={{ gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
            Um domínio precisa levar a algum lugar. Publique sua primeira página e a gente já mostra
            os nomes livres para o seu negócio.
          </Typography>
          <Benefits benefits={(vm.benefits || []).slice(0, 2)} compact />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
            <Button
              onClick={() => a.onCreatePage?.()}
              disabled={!!vm.busy}
              disableElevation
              startIcon={<PostAddRounded sx={{ fontSize: 20 }} />}
              sx={ACTION_BTN_SX}
            >
              Publicar uma página
            </Button>
            {vm.dismissible && (
              <Button onClick={() => a.onDismiss?.()} disabled={!!vm.busy} sx={QUIET_BTN_SX}>
                Agora não
              </Button>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            Quando a página estiver no ar: domínio próprio por {vm.price?.label} {vm.price?.periodLabel}.
          </Typography>
        </Panel>
      </>
    );
  }

  /* ── offer / awaiting_payment / owned ───────────────────────────────── */
  const domains = vm.domains || [];
  const showUpdated = state === 'awaiting_payment' || state === 'owned';

  return (
    <>
      {shell(
        <>
          <CardHead vm={vm} a={a} showUpdated={showUpdated} selling={state === 'offer'} />
          <FeedbackBar feedback={vm.actionFeedback} busy={vm.busy} a={a} />

          {/* B — pagamento em aberto ganha de tudo: a oferta não aparece aqui de propósito */}
          {vm.payment && <PaymentBlock vm={vm} payment={vm.payment} a={a} />}

          {/* A — o que já é da conta */}
          {domains.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {domains.map((d) => <DomainPanel key={d.id} vm={vm} domain={d} a={a} />)}
            </Box>
          )}

          {/* C — a oferta. Em `owned` ela vem depois dos domínios: comprar um segundo é o
              assunto menos urgente da tela. */}
          {vm.offer && (
            domains.length > 0 ? (
              <Box sx={{ pt: 'var(--space-1)', borderTop: '1px solid', borderColor: 'var(--divider)' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
                  <SectionTitle hint="Dá para ter mais de um endereço apontando para as suas páginas.">
                    Quer outro domínio?
                  </SectionTitle>
                  <Button
                    onClick={() => setShowOffer((v) => !v)}
                    aria-expanded={showOffer}
                    sx={{ ...OUTLINE_BTN_SX, borderColor: CTA_FILL, color: CTA_FILL_DARK, '&:hover': { borderColor: CTA_FILL, color: CTA_FILL_DARK, bgcolor: CTA_SOFT } }}
                  >
                    {showOffer ? 'Fechar' : 'Ver nomes livres'}
                  </Button>
                </Box>
                <Collapse in={showOffer} unmountOnExit>
                  <Box sx={{ pt: 'var(--space-2)' }}>
                    <OfferBlock vm={vm} offer={vm.offer} a={a} />
                  </Box>
                </Collapse>
              </Box>
            ) : (
              <OfferBlock vm={vm} offer={vm.offer} a={a} />
            )
          )}
        </>
      )}

      <CancelDialog dialog={vm.cancelDialog} a={a} />
      <RootPageDialog dialog={vm.rootPageDialog} a={a} />
    </>
  );
}
